/**
 * Admin > Email Templates
 * Edit the subject and body of client-facing notification emails.
 * Body is stored as Markdown and rendered to HTML at send time.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import {
  listEmailTemplates,
  updateEmailTemplate,
  resetEmailTemplate,
  testSendEmailTemplate,
  createEmailTemplate,
  deleteEmailTemplate,
  listSystemMessages,
  previewEmailTemplate,
} from '../../services/admin'
import type { EmailTemplate, EmailTemplateVariable, SystemMessage } from '../../types'
import Modal from '../../components/Modal'

// ---------------------------------------------------------------------------
// Minimal markdown editor (no external packages — toolbar + textarea + preview)
// The @uiw/react-md-editor package is added to package.json for Docker rebuild
// but since it requires npm install we use a lightweight custom editor here.
// ---------------------------------------------------------------------------

const TOOLBAR_ACTIONS = [
  { label: 'B', title: 'Bold', wrap: ['**', '**'] },
  { label: 'I', title: 'Italic', wrap: ['*', '*'] },
  { label: '•', title: 'Bullet list', prefix: '- ' },
  { label: '1.', title: 'Numbered list', prefix: '1. ' },
] as const

interface MarkdownEditorProps {
  value: string
  onChange: (v: string) => void
  variables?: EmailTemplateVariable[]
}

function MarkdownEditor({ value, onChange, variables }: MarkdownEditorProps) {
  const [showPreview, setShowPreview] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function insertWrap(before: string, after: string) {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end)
    const newText = value.slice(0, start) + before + selected + after + value.slice(end)
    onChange(newText)
    setTimeout(() => {
      ta.focus()
      ta.setSelectionRange(start + before.length, start + before.length + selected.length)
    }, 0)
  }

  function insertPrefix(prefix: string) {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const newText = value.slice(0, lineStart) + prefix + value.slice(lineStart)
    onChange(newText)
    setTimeout(() => {
      ta.focus()
      ta.setSelectionRange(start + prefix.length, start + prefix.length)
    }, 0)
  }

  function insertVariable(name: string) {
    const ta = textareaRef.current
    if (!ta) return
    const pos = ta.selectionStart
    const insertion = `{{ ${name} }}`
    const newText = value.slice(0, pos) + insertion + value.slice(pos)
    onChange(newText)
    setTimeout(() => {
      ta.focus()
      ta.setSelectionRange(pos + insertion.length, pos + insertion.length)
    }, 0)
  }

  // Simple client-side markdown preview (no external package required)
  const renderPreview = useCallback((md: string) => {
    return md
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-4 mb-2">$1</h1>')
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold mt-3 mb-2">$1</h2>')
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-2 mb-1">$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-blue-600 underline">$1</a>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
      .replace(/\{\{ (.+?) \}\}/g, '<span class="bg-blue-100 text-blue-800 px-1 rounded text-xs font-mono">{{ $1 }}</span>')
      .replace(/\n\n/g, '</p><p class="mb-3">')
      .replace(/\n/g, '<br>')
  }, [])

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border border-gray-200 rounded-t bg-gray-50 px-2 py-1">
        {TOOLBAR_ACTIONS.map((action) => (
          <button
            key={action.label}
            type="button"
            title={action.title}
            className="px-2 py-0.5 text-sm font-medium text-gray-600 rounded hover:bg-gray-200 transition-colors"
            onClick={() => {
              if ('wrap' in action) insertWrap(action.wrap[0], action.wrap[1])
              else insertPrefix(action.prefix)
            }}
          >
            {action.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          className={`tap px-2 py-0.5 text-xs rounded transition-colors ${showPreview ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-200'}`}
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? 'Edit' : 'Preview'}
        </button>
      </div>

      {showPreview ? (
        <div
          className="border border-gray-200 rounded-b p-3 min-h-[220px] prose prose-sm max-w-none text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: `<p class="mb-3">${renderPreview(value)}</p>` }}
        />
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded-b p-3 font-mono text-sm leading-relaxed min-h-[220px] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
          spellCheck={false}
        />
      )}

      {/* Variable chips */}
      {variables && variables.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1">Click a variable to insert at cursor:</p>
          <div className="flex flex-wrap gap-1">
            {variables.map((v) => (
              <button
                key={v.name}
                type="button"
                title={v.description}
                className="tap px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-800 rounded text-xs font-mono hover:bg-blue-100 transition-colors"
                onClick={() => insertVariable(v.name)}
              >
                {'{{ '}
                {v.name}
                {' }}'}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AdminEmailTemplatesPage() {
  const { user } = useAuth()
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // Sign-in and account-security messages, listed so they are findable. They are
  // not rows and cannot be edited — see the note rendered above the list.
  const [systemMessages, setSystemMessages] = useState<SystemMessage[]>([])
  const [showSystem, setShowSystem] = useState(false)

  // Edit modal
  const [editTarget, setEditTarget] = useState<EmailTemplate | null>(null)
  const [editSubject, setEditSubject] = useState('')
  const [editBody, setEditBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Preview modal
  const [previewTarget, setPreviewTarget] = useState<EmailTemplate | null>(null)
  const [previewContent, setPreviewContent] = useState<{ subject: string; html_body: string } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')

  // Reset confirm
  const [resetTarget, setResetTarget] = useState<EmailTemplate | null>(null)
  const [resetting, setResetting] = useState(false)

  // Create a new message, and delete one that staff added.
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newSubject, setNewSubject] = useState('')
  const [newBody, setNewBody] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Send a real copy to a staff address. A browser preview cannot show how a
  // mail client will treat the message, which is exactly how the collapsed
  // line breaks went unnoticed.
  const [testTarget, setTestTarget] = useState<EmailTemplate | null>(null)
  const [testEmail, setTestEmail] = useState('')
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState('')
  const [testError, setTestError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      setTemplates(await listEmailTemplates())
      try {
        setSystemMessages(await listSystemMessages())
      } catch {
        // Reference-only: a failure hides the list rather than breaking the page.
        setSystemMessages([])
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  function openEdit(t: EmailTemplate) {
    setEditTarget(t)
    setEditSubject(t.subject_template)
    setEditBody(t.body_markdown)
    setSaveError('')
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!editTarget) return
    setSaving(true)
    setSaveError('')
    try {
      const updated = await updateEmailTemplate(editTarget.template_key, {
        subject_template: editSubject.trim(),
        body_markdown: editBody.trim(),
      })
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
      setEditTarget(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function openPreview(t: EmailTemplate, subject?: string, body?: string) {
    setPreviewTarget(t)
    setPreviewContent(null)
    setPreviewError('')
    setPreviewLoading(true)
    try {
      const result = await previewEmailTemplate(t.template_key, {
        subject_template: subject,
        body_markdown: body,
      })
      setPreviewContent(result)
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      setPreviewLoading(false)
    }
  }

  async function handleReset() {
    if (!resetTarget) return
    setResetting(true)
    try {
      const updated = await resetEmailTemplate(resetTarget.template_key)
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
      setResetTarget(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Reset failed')
    } finally {
      setResetting(false)
    }
  }

  function openCreate() {
    setNewName('')
    setNewDescription('')
    setNewSubject('')
    setNewBody('')
    setCreateError('')
    setShowCreate(true)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)
    setCreateError('')
    try {
      const created = await createEmailTemplate({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        subject_template: newSubject.trim(),
        body_markdown: newBody,
      })
      setTemplates((prev) => [...prev, created])
      setShowCreate(false)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create the message')
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteEmailTemplate(deleteTarget.template_key)
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  async function openTestSend(t: EmailTemplate) {
    setTestTarget(t)
    setTestEmail(user?.email ?? '')
    setTestResult('')
    setTestError('')
  }

  async function handleTestSend(e: React.FormEvent) {
    e.preventDefault()
    if (!testTarget) return
    setTestSending(true)
    setTestResult('')
    setTestError('')
    try {
      const res = await testSendEmailTemplate(testTarget.template_key, {
        recipient_email: testEmail.trim() || undefined,
      })
      setTestResult(res.detail)
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Could not send the test message')
    } finally {
      setTestSending(false)
    }
  }

  async function handleToggleSignature(t: EmailTemplate, include: boolean) {
    try {
      const updated = await updateEmailTemplate(t.template_key, { include_signature: include })
      setTemplates((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Update failed')
    }
  }

  async function handleToggleActive(t: EmailTemplate) {
    try {
      const updated = await updateEmailTemplate(t.template_key, { is_active: !t.is_active })
      setTemplates((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Update failed')
    }
  }

  if (!user?.is_superuser) return <Navigate to="/dashboard" replace />

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Messages</h2>
          <p className="mt-1 text-sm text-gray-500">
            The messages sent to clients. Edit the wording, add your own, and send yourself a
            test to see how one arrives.
            Use <code className="bg-gray-100 px-1 rounded text-xs">{'{{ variable }}'}</code> where you
            want a client&rsquo;s details filled in.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ New Message</button>
      </div>

      {loadError && <div className="alert alert-error mb-4">{loadError}</div>}

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="space-y-6">
          {[
            {
              key: 'builtin',
              heading: 'Built-in messages',
              blurb:
                'These came with the app. Edit the wording to suit you — Reset puts any of them ' +
                'back to how it shipped. A few cannot be deleted, because the app sends them by ' +
                'name or a job is pointing at them; those have no Delete button.',
              rows: templates.filter((t) => t.is_system),
            },
            {
              key: 'yours',
              heading: 'Your messages',
              blurb:
                'Messages you have written. Pick one of these — or a built-in — when you send from ' +
                'a client or lead page.',
              rows: templates.filter((t) => !t.is_system),
            },
          ].map((group) =>
            group.rows.length === 0 ? null : (
              <section key={group.key}>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {group.heading}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5 mb-3">{group.blurb}</p>
                <div className="space-y-4">
                  {group.rows.map((t) => (
                    <div key={t.id} className={`card ${!t.is_active ? 'opacity-60' : ''}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-gray-900">{t.name}</h3>
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {t.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {t.is_system && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">
                                Built-in
                              </span>
                            )}
                          </div>
                          {t.description && (
                            <p className="text-sm text-gray-500 mt-0.5">{t.description}</p>
                          )}
                          <div className="mt-2 text-sm font-mono text-gray-600 bg-gray-50 rounded px-3 py-1.5 border border-gray-200 truncate">
                            <span className="text-gray-400 text-xs mr-1">Subject:</span>
                            {t.subject_template}
                          </div>
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <label className="flex items-center gap-2 text-xs text-gray-600 ml-2">
                              <input
                                type="checkbox"
                                checked={t.include_signature}
                                onChange={(e) => handleToggleSignature(t, e.target.checked)}
                              />
                              Sign with the sender&rsquo;s business card
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openPreview(t)}
                          >
                            Preview
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openTestSend(t)}
                          >
                            Send test
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleToggleActive(t)}
                          >
                            {t.is_active ? 'Disable' : 'Enable'}
                          </button>
                          {/* Two independent questions, and they used to be one.
                              A built-in message can now be BOTH resettable and
                              deletable — the reference-copy pair is exactly that:
                              it ships with wording to go back to, and nothing
                              addresses it by name. Asking `is_system` here is what
                              made "built-in" and "deletable" mutually exclusive. */}
                          {t.can_reset && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setResetTarget(t)}
                            >
                              Reset
                            </button>
                          )}
                          {t.can_delete && (
                            <button
                              className="btn btn-secondary btn-sm text-gray-500 hover:text-red-600 hover:border-red-200"
                              onClick={() => setDeleteTarget(t)}
                            >
                              Delete
                            </button>
                          )}
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => openEdit(t)}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )
          )}
        </div>
      )}

      {/* Sign-in and account messages — listed, not editable. */}
      {systemMessages.length > 0 && (
        <div className="mt-8">
          <button
            type="button"
            className="text-sm text-gray-600 hover:text-gray-900 underline"
            onClick={() => setShowSystem((v) => !v)}
            aria-expanded={showSystem}
          >
            {showSystem ? 'Hide' : 'Show'} sign-in and account messages ({systemMessages.length})
          </button>
          {showSystem && (
            <div className="mt-3">
              <p className="text-sm text-gray-500 mb-3">
                These are sent automatically for sign-in, account security and FCC update
                problems. They cannot be edited: several carry a live sign-in or password-reset
                link, so their wording is part of keeping accounts secure.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4 font-semibold">Message</th>
                      <th className="py-2 pr-4 font-semibold">Subject</th>
                      <th className="py-2 pr-4 font-semibold">Sent</th>
                      <th className="py-2 font-semibold">Goes to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemMessages.map((m) => (
                      <tr key={m.key} className="border-b border-gray-100 align-top">
                        <td className="py-2 pr-4 text-gray-900">{m.name}</td>
                        <td className="py-2 pr-4 text-gray-600">{m.subject}</td>
                        <td className="py-2 pr-4 text-gray-500">{m.when}</td>
                        <td className="py-2 text-gray-500">{m.audience}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* New message */}
      {showCreate && (
        <Modal
          titleId="create-email-template-title"
          title="New Message"
          onClose={() => setShowCreate(false)}
          showCloseButton
        >
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="label" htmlFor="new-tmpl-name">Name *</label>
              <input
                id="new-tmpl-name"
                type="text"
                className="input"
                required
                maxLength={100}
                placeholder="e.g. Welcome — new client"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <p className="text-xs text-gray-400 mt-1">Only you and your colleagues see this.</p>
            </div>
            <div>
              <label className="label" htmlFor="new-tmpl-desc">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="new-tmpl-desc"
                type="text"
                className="input"
                maxLength={500}
                placeholder="When would you use this message?"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="new-tmpl-subject">Subject *</label>
              <input
                id="new-tmpl-subject"
                type="text"
                className="input"
                required
                maxLength={255}
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Message *</label>
              <MarkdownEditor value={newBody} onChange={setNewBody} />
            </div>
            {createError && <div className="alert alert-error">{createError}</div>}
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating || !newName.trim() || !newSubject.trim() || !newBody.trim()}
              >
                {creating ? 'Creating…' : 'Create Message'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <Modal
          titleId="delete-email-template-title"
          title={`Delete "${deleteTarget.name}"?`}
          onClose={() => setDeleteTarget(null)}
          showCloseButton
        >
          <p className="text-sm text-gray-600">
            This message will be removed permanently. Anything already sent is unaffected.
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}

      {/* Send a test copy */}
      {testTarget && (
        <Modal
          titleId="test-send-title"
          title={`Send a test: ${testTarget.name}`}
          onClose={() => setTestTarget(null)}
          showCloseButton
        >
          <form onSubmit={handleTestSend} className="space-y-4">
            <p className="text-sm text-gray-600">
              Sends this message to the address below using sample values, so you can
              see how it arrives. The subject is marked as a test.
            </p>
            <div>
              <label className="label" htmlFor="test-send-email">Send to</label>
              <input
                id="test-send-email"
                type="email"
                className="input"
                required
                maxLength={255}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            {testResult && <div className="alert alert-success">{testResult}</div>}
            {testError && <div className="alert alert-error">{testError}</div>}
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-secondary" onClick={() => setTestTarget(null)}>
                Close
              </button>
              <button type="submit" className="btn btn-primary" disabled={testSending}>
                {testSending ? 'Sending…' : 'Send test'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit modal */}
      {editTarget && (
        <Modal
          titleId="edit-email-template-title"
          title={`Edit: ${editTarget.name}`}
          onClose={() => setEditTarget(null)}
          showCloseButton
        >
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="label">Subject</label>
              <input
                type="text"
                className="input font-mono text-sm"
                required
                maxLength={255}
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
              />
            </div>

            <div>
              <label className="label">Body (Markdown)</label>
              <MarkdownEditor
                value={editBody}
                onChange={setEditBody}
                variables={editTarget.available_variables ?? []}
              />
            </div>

            {saveError && <div className="alert alert-error">{saveError}</div>}

            <div className="flex justify-between gap-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => openPreview(editTarget, editSubject, editBody)}
              >
                Preview Current Edits
              </button>
              <div className="flex gap-3">
                <button type="button" className="btn btn-secondary" onClick={() => setEditTarget(null)}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn btn-primary">
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Preview modal */}
      {previewTarget && (
        <Modal
          titleId="preview-email-template-title"
          title={`Preview: ${previewTarget.name}`}
          onClose={() => setPreviewTarget(null)}
          showCloseButton
        >
          <div className="space-y-3">
            {previewLoading && <div className="text-gray-500 py-4">Rendering preview…</div>}
            {previewError && <div className="alert alert-error">{previewError}</div>}
            {previewContent && (
              <>
                <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm">
                  <span className="text-gray-400 text-xs font-medium mr-1">SUBJECT:</span>
                  {previewContent.subject}
                </div>
                <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Rendered email:</div>
                <iframe
                  srcDoc={previewContent.html_body}
                  title="Email preview"
                  className="w-full border border-gray-200 rounded"
                  style={{ height: '480px' }}
                  sandbox="allow-same-origin"
                />
                <p className="text-xs text-gray-400">
                  Dummy data is used for preview. Company branding comes from Notification Settings.
                </p>
              </>
            )}
            <div className="flex justify-end">
              <button className="btn btn-secondary" onClick={() => setPreviewTarget(null)}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset confirm modal */}
      {resetTarget && (
        <Modal
          titleId="reset-email-template-title"
          title="Reset to Default"
          onClose={() => setResetTarget(null)}
          showCloseButton
        >
          <p className="text-gray-700 mb-4">
            Reset <strong>{resetTarget.name}</strong> to its factory default subject and body?
            Your customizations will be lost.
          </p>
          <div className="flex justify-end gap-3">
            <button className="btn btn-secondary" onClick={() => setResetTarget(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleReset} disabled={resetting}>
              {resetting ? 'Resetting…' : 'Reset to Default'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
